<template>
  <div>

    <hr>
    <!-- add/edit Business Rule form -->
    <v-dialog v-model="dialog" max-width="700px" persistent scrollable>
      <div slot="activator">
        <v-tooltip right>
          <v-btn fab large color="primary darken-1" dark slot="activator" class=""> <!-- elevation-10 -->
            <v-icon>add</v-icon>
          </v-btn>
          <span>Add a new business rule</span>
        </v-tooltip>
      </div>

      <v-card>
        <v-card-title class="headline grey lighten-2">
          <span class="headline">{{ formTitle }}</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text style="max-height: 600px;">
          <v-container grid-list-md class="px-0 py-0">
            <v-layout wrap>
              <v-flex xs12>
                <v-select
                  :items="ruleTypes" v-model="editedItem.type"
                  label="Rule Type" name="type"
                  auto :disabled="editedIndex !== -1"
                  required
                ></v-select>
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  label="Name"
                  name="name"
                  v-model="editedItem.name"
                  required
                ></v-text-field>
              </v-flex>
              <v-flex xs12 v-for="(channel, cIndex) in channels" :key="'chnl'+cIndex">
                <v-text-field
                  :label="channel.name + ' Benchmark'"
                  v-model="editedItem.ruleBenchmarks[cIndex].benchmark"
                  append-icon="%"
                  type="number"
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-combobox
                  v-model="editedItem.businessRuleSlotClassifications"
                  :items="slotCategories"
                  label="Seat Categories"
                  chips
                  clearable
                  solo
                  multiple
                  item-text="slotCategory.name"
                >
                  <template slot="selection" slot-scope="data">
                    <v-chip
                      :selected="data.selected"
                      close
                      @input="removeSlotCategory(data.item)"
                    >
                      <strong>{{ data.item.slotCategory.name }}</strong>&nbsp;
                      <span>(seat)</span>
                    </v-chip>
                  </template>
                </v-combobox>
              </v-flex>
              <v-flex xs12>
                <v-textarea outline
                  rows="3"
                  label="Description"
                  name="description"
                  v-model="editedItem.description"
                ></v-textarea>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn color="primary darken-1" flat @click.native="close('edit')">
            <v-icon left dark>arrow_back</v-icon>
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary darken-1" @click.native="save" :loading="formLoading">
            <v-icon left dark>save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End add/edit Business Rule form -->

    <!-- business rules data grid -->
    <v-container fluid grid-list-md class="px-1">
      <v-data-iterator
        :items="businessRules"
        :rows-per-page-items="rowsPerPageItems"
        :pagination.sync="pagination"
        content-tag="v-layout"
        row
        wrap
      >
        <v-toolbar
          slot="header"
          class="mb-2"
          color="primary darken-1"
          dark
          flat
        >
          <v-toolbar-title>Seat Recommendation Business Rules</v-toolbar-title>
        </v-toolbar>
        <v-flex
          slot="item"
          slot-scope="props"
          xs12
          sm6
          md4
          lg3
        >
          <v-card class="elevation-5">
            <v-card-title class="py-1">
              <h4>{{ props.item.name }}</h4>
              <v-spacer></v-spacer>
              <span>
                <v-tooltip top>
                  <v-btn icon class="mx-0" @click="editItem(props.item)" slot="activator">
                    <v-icon color="teal">edit</v-icon>
                  </v-btn>
                  <span>Edit</span>
                </v-tooltip>
                <v-tooltip top>
                  <v-btn icon class="mx-0" @click="deleteItem(props.item)" slot="activator">
                    <v-icon color="pink">delete</v-icon>
                  </v-btn>
                  <span>Delete</span>
                </v-tooltip>
              </span>
            </v-card-title>
            <v-divider></v-divider>
            <v-list dense>
              <v-list-tile>
                <v-list-tile-content>Rule Number:</v-list-tile-content>
                <v-list-tile-content class="align-end">{{ props.item.id }}</v-list-tile-content>
              </v-list-tile>
              <v-list-tile>
                <v-list-tile-content>Rule Type:</v-list-tile-content>
                <v-list-tile-content class="align-end">{{ props.item.type }}</v-list-tile-content>
              </v-list-tile>
              <v-list-tile
                v-for="(benchmark, bIndex) in props.item.ruleBenchmarks"
                :key="'bmrk_'+benchmark.id+bIndex"
              >
                <v-list-tile-content>{{ benchmark.channel.name }} Benchmark:</v-list-tile-content>
                <v-list-tile-content class="align-end">{{ benchmark.benchmark }}</v-list-tile-content>
              </v-list-tile>
            </v-list>
            <v-card-title primary-title class="py-0">
              <span>Seat Categories:</span>
              <v-spacer></v-spacer>
              <span>
                <v-chip
                  color="grey"
                  text-color="white"
                  v-for="(seatCategory, sIndex) in props.item.businessRuleSlotClassifications"
                  :key="'scat_'+seatCategory.id+sIndex"
                >
                  {{ seatCategory.slotCategory.name }}
                </v-chip>
              </span>
            </v-card-title>
            <v-card-title primary-title class="py-0">
              <v-flex xs12 class="py-0 px-0">
                <v-textarea outline
                            rows="3"
                            label="Description"
                            name="description"
                            v-model="props.item.description"
                            readonly
                ></v-textarea>
              </v-flex>
            </v-card-title>
          </v-card>
        </v-flex>
      </v-data-iterator>
    </v-container>
    <!-- end business rules data grid -->

    <!-- snackbar -->
    <v-snackbar
      :timeout="snackbarTimeout"
      :color="snackbarColor"
      v-model="snackbar"
      top
    >
      {{ snackbarText }}
      <v-btn dark flat @click.native="snackbar = false">Close</v-btn>
    </v-snackbar>
    <!-- end snackbar -->
  </div>
</template>

<script>
  export default {
    name: "BusinessRules",

    data: () => ({
      rowsPerPageItems: [4, 8, 12],
      pagination: {
        rowsPerPage: 4
      },
      businessRules: [],
      noBusinessRulesFound: false,
      ruleTypes: ['NO_ODD_SEATS'],
      channels: [],
      slotCategories: [],



      tableLoading: true,
      editedIndex: -1,
      dialog: false,
      formLoading: false,
      snackbar: false,
      snackbarColor: '',
      snackbarMode: '',
      snackbarTimeout: 6000,
      snackbarText: '',
      editedItem: {
        name: "",
        description: "",
        type: "",
        businessRuleSlotClassifications: [],
        ruleBenchmarks: []
      },
      defaultItem: {
        name: "",
        description: "",
        type: "",
        businessRuleSlotClassifications: [],
        ruleBenchmarks: []
      },
      form: new Form({
        name: "",
        description: "",
        type: "",
        businessRuleSlotClassifications: [],
        ruleBenchmarks: []
      }),
    }),

    computed: {
      formTitle () {
        return this.editedIndex === -1 ? 'New Business Rule' : 'Edit Business Rule';
      }
    },

    watch: {
      dialog (val) {
        val || this.close('edit');
      }
    },

    created() {
      this.getBusinessRules();
      this.getChannels();
      this.getSlotCategories();
    },

    methods: {
      /* Fetch the list of all business rules from the server */
      getBusinessRules() {
        axios.get(BACKEND_URL+'/api/business-rules').then(({data})=>{
          this.businessRules = data;
          this.noBusinessRulesFound = (! this.businessRules.length > 0);
          this.tableLoading = false;
          //   console.log(data);
        }).catch((error)=>{
          this.tableLoading = false;
          if (error.message === 'Network Error') {
            this.displayErrorMessages('Cannot connect to server');
          } else {
            this.displayErrorMessages(error.response.data);
          }
        });
      },

      /* Fetch the list of all Channels form the server */
      getChannels() {
        axios.get(BACKEND_URL+'/api/channels').then(({data})=>{
          this.channels = data;
          for (let i = 0; i < this.channels.length; i++) {
            let ruleBenchmark = {};
            ruleBenchmark.benchmark = null;
            ruleBenchmark.channel = { code: this.channels[i].code };
            this.editedItem.ruleBenchmarks.push(ruleBenchmark);
          }
          this.defaultItem.ruleBenchmarks = JSON.parse(JSON.stringify(this.editedItem.ruleBenchmarks));
          // console.log();
        }).catch((error)=>{
          if (error.message === 'Network Error') {
            this.displayErrorMessages('Cannot connect to server');
          } else {
            this.displayErrorMessages(error.response.data);
          }
        });
      },

      /* Fetch the list of all Slot Categories form the server */
      getSlotCategories() {
        axios.get(BACKEND_URL+'/api/slot/categories').then(({data})=>{
          for (let i = 0; i < data.length; i++) {
            let businessRuleSlotClassification = {};
            businessRuleSlotClassification.slotCategory = data[i];
            this.slotCategories.push(businessRuleSlotClassification);
          }
          // console.log(data);
        }).catch((error)=>{
          if (error.message === 'Network Error') {
            this.displayErrorMessages('Cannot connect to server');
          } else {
            this.displayErrorMessages(error.response.data);
          }
        });
      },

      /* Remove items from the chip */
      removeSlotCategory (item) {
        this.editedItem.businessRuleSlotClassifications.splice(this.editedItem.businessRuleSlotClassifications.indexOf(item), 1);
        this.editedItem.businessRuleSlotClassifications = [...this.editedItem.businessRuleSlotClassifications];
      },

      /* Pass the selected item to the edit dialog and show the dialog */
      editItem (item) {
        // console.log(item);
        this.editedIndex = this.businessRules.indexOf(item);
        let tempItem = JSON.parse(JSON.stringify(item));
        tempItem.ruleBenchmarks = [];
        for (let i = 0; i < this.channels.length; i++) {
          let ruleBenchmark = {};
          ruleBenchmark.benchmark = this.getBenchmarkByChannelCode(item.ruleBenchmarks, this.channels[i].code);
          ruleBenchmark.channel = { code: this.channels[i].code };
          tempItem.ruleBenchmarks.push(ruleBenchmark);
        }
        this.editedItem = JSON.parse(JSON.stringify(tempItem));

        this.dialog = true;
      },

      /* Delete an item */
      deleteItem (item) {
        const index = this.businessRules.indexOf(item);
        let prompt = confirm("Are you sure you want to permanently delete this business rule from the database? \nThis action cannot be undone.");
        if (prompt === true) {
          axios.delete(BACKEND_URL+'/api/business-rules/' + item.id).then(({data})=>{
            this.businessRules.splice(index, 1);
            console.log(data);
          }).catch((error)=>{
            if (error.message === 'Network Error') {
              console.log('Cannot connect to server');
            } else {
              console.log(error.response.data);
            }
          });
        }
      },

      /* Reset the new/edit dialog form when it closes */
      close(modal) {
        if (modal === 'edit') {
          this.dialog = false;
        } else if (modal === 'booking') {
          this.bookingDialog = false;
        }
        setTimeout(() => {
          // this.editedItem = Object.assign({}, this.defaultItem);
          this.editedItem = JSON.parse(JSON.stringify(this.defaultItem));
          this.editedIndex = -1;
        }, 300);
      },

      /* Save/edit dialogue form content */
      save () {
        this.formLoading = true;

        if (this.editedIndex > -1) {
          // console.log(this.editedItem);
          for (let i = 0; i < this.editedItem.businessRuleSlotClassifications.length; i++) {
            this.editedItem.businessRuleSlotClassifications[i].id = null;
          }

          axios.put(BACKEND_URL+'/api/business-rules/'+this.editedItem.id, this.editedItem).then(({data}) => {
            console.log(data);
            Object.assign(this.businessRules[this.editedIndex], data);

            this.formLoading = false;
            this.close('edit');
            this.showSnackbar('success', 'Event details successfully updated!');
          }).catch((error) => {
            this.displayErrorMessages(error);
          });
        } else {
          axios.post(BACKEND_URL+'/api/business-rules', this.editedItem).then(({data}) => {
            console.log(data);
            this.businessRules.push(data);

            this.formLoading = false;
            this.close('edit');
            this.showSnackbar('success', 'The resource has been successfully created!');
            // this.layouts = [];

          }).catch((error) => {
            this.displayErrorMessages(error);
          });
        }
      },

      /* Display a message in a snackbar */
      showSnackbar(snackbarColor, snackbarText) {
        this.snackbarColor = snackbarColor;
        this.snackbarText = snackbarText;
        this.snackbar = true;
      },

      /* Handle error messages */
      displayErrorMessages(errorMsg) {
        console.log(errorMsg);
        this.showSnackbar('error', errorMsg);
        this.formLoading = false;
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/settings/BusinessRules.vue